import React from 'react';
import { useParams } from 'react-router-dom';
import { Label } from '@progress/kendo-react-labels';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';

import {
  GetZoneById,
  UpdateZoneAsync,
  UpdateGameDocState,
  UpdateResourceAsync
} from '../../utils/game-document';
import { StandardTextarea } from '../forms';
import { GameDocumentContext } from '../../contexts/game-document';
import { ResourceEntity, ZoneEntity } from '../../types/game-document/entities';

export const ZoneMapDetails: React.FC = () => {
  const { zoneId } = useParams();
  const [state, setState] = React.useContext(GameDocumentContext);
  const { gameDocument } = state;

  const getEntityById = (entityId: string) =>
    state?.gameDocument?.assets?.zones !== undefined
      ? state.gameDocument?.assets.zones?.find((i) => i.id === entityId)
      : undefined;

  const [zoneDescription, setZoneDescription] = React.useState<string>('');
  const [resources, setResources] = React.useState<ResourceEntity[]>([]);
  const [entity, setEntity] = React.useState(() => getEntityById(zoneId!));
  const [showZoneTitle, setShowZoneTitle] = React.useState(
    entity?.isTitleVisible ?? false
  );

  const handleInputChange = async (event: InputChangeEvent) => {
    if (event.target.name === 'titleResId') {
      const { name, value } = event.target;
      setResources(
        resources.map((item) =>
          item.id === entity![name]
            ? { ...item, value: value! as string }
            : { ...item }
        )
      );
      const resource = resources.find(
        (item) => item.id === entity?.titleResId!
      )!;
      resource.value = value as string;

      const selectedZone = GetZoneById(state.gameDocument, zoneId ?? '');
      if (selectedZone) {
        const zoneTitleElement = document.getElementById(
          `labelName-zone-${selectedZone.boundary.id}`
        );

        if (zoneTitleElement) zoneTitleElement.innerHTML = value as string;
      }

      UpdateResourceAsync(state.gameDocument!, resource?.id, resource).then(
        (updatedGameDocument) => {
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        }
      );
    } else {
      setZoneDescription(event.value);
      await UpdateZoneAsync(state.gameDocument!, zoneId!, {
        ...entity!,
        [event.target.name!]: event.target.value
      }).then((updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));
      });
    }
  };

  React.useEffect(() => {
    if (zoneId) {
      let zone = gameDocument?.assets?.zones?.find((z) => z.id === zoneId);
      if (zone) {
        setZoneDescription(zone?.description ?? '');
      }
    }

    let zones: ZoneEntity[] = [];
    zones.push(
      gameDocument?.assets?.zones?.find((z) => z.id === zoneId) as ZoneEntity
    );

    const zone = getEntityById(zoneId ?? '');
    if (zone) {
      const zoneRes = state.gameDocument?.resources.find(
        (item) => item.id === zone.titleResId!
      )! as ResourceEntity;
      if (zoneRes !== undefined) {
        setEntity(zone);
        setResources([zoneRes]);
      }
    }
  }, [zoneId, state]);

  const onShowZoneTitle = (isVisible: boolean) => {
    if (gameDocument) {
      const newGame = { ...gameDocument };
      const { zones } = gameDocument.assets;

      if (zones) {
        const zoneIndex = zones.findIndex((x) => x.id === zoneId);
        zones[zoneIndex].isTitleVisible = !isVisible;
        setShowZoneTitle(!isVisible);
        newGame.assets.zones = zones;
        setState((state) => UpdateGameDocState(state, newGame));
      }
    }
  };

  return (
    <div
      className={'d-flex flex-column'}
      style={{
        marginTop: '8px',
        gap: '8px'
      }}>
      <span
        style={{
          fontSize: '12px'
        }}>
        This is what the player sees. Show or hide it to the player using the
        eye icon.
      </span>

      <div>
        <Label>Title</Label>
        <div className={'d-flex gap-2 w-100'}>
          <Input
            name={'titleResId'}
            value={
              resources.find((resource) => resource.id === entity?.titleResId)
                ?.value ?? ''
            }
            onChange={(e) => handleInputChange(e)}
          />

          <div
            onClick={() => onShowZoneTitle(showZoneTitle)}
            className={'d-flex align-items-center justify-content-center'}>
            <span
              className={`${showZoneTitle ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
              {showZoneTitle ? 'visibility' : 'visibility_off'}
            </span>
          </div>
        </div>
      </div>

      <span
        style={{
          fontSize: '12px'
        }}>
        These help you identify this zone in files, the player won’t see it
      </span>
      <Label>Description</Label>
      <StandardTextarea
        name={'description'}
        value={zoneDescription}
        onChange={(e) => handleInputChange(e as any)}
      />
    </div>
  );
};
